import { Direction, Vocation } from '@game/protocol';

import { FRAME_HEIGHT, FRAME_WIDTH, characterSheet } from './art/characters.ts';
import { CREATURE_COLUMNS, CREATURE_ROWS, creatureSheet } from './art/creatures.ts';

/**
 * A arte da página inicial, desenhada pelo mesmo código que desenha o jogo.
 *
 * A alternativa era exportar PNG dos sprites e versioná-los em `public/`. Seria menos
 * código e teria um defeito que não dá para consertar depois: **a página passaria a
 * mentir**. Toda vez que o traço de uma vocação mudasse, o site continuaria mostrando a
 * versão antiga até alguém lembrar de reexportar — e quem chega pela página inicial ia ver
 * um personagem que não existe mais dentro do jogo. Chamando `characterSheet` e
 * `creatureSheet` aqui, a página é sempre o estado atual da arte, de graça.
 *
 * É barato porque estes três módulos de arte não dependem do Phaser em tempo de execução —
 * o `import type` em `art/canvas.ts`, `art/characters.ts` e `art/creatures.ts` existe para
 * isto: o pacote desta página tem o desenho e não tem a engine.
 *
 * O elemento diz **o que** ele é (`data-vocacao="0"`, `data-bicho="37"`) e o tamanho sai da
 * própria folha. Assim a medida de cada bicho — o Rato tem 24 px, o Lich tem 64 — mora num
 * lugar só, que é `art/creatures.ts`, e a página não tem número de pixel escrito à mão.
 */

/** Colunas da caminhada na folha do personagem (ver `art/characters.ts`). */
const WALK_COLUMNS = [2, 3, 4, 5];
/** Os dois quadros de repouso: é a alternância deles que faz o bicho respirar. */
const IDLE_COLUMNS = [0, 1];

/**
 * Recorta colunas de uma linha da folha e devolve como imagem embutida.
 *
 * Uma tira, e não um quadro por imagem: a animação da página é uma faixa de fundo que anda
 * em `steps()`, e uma tira única evita quatro requisições e quatro decodificações.
 */
function strip(
  sheet: HTMLCanvasElement,
  frameWidth: number,
  frameHeight: number,
  columns: readonly number[],
  row: number,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = frameWidth * columns.length;
  canvas.height = frameHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.imageSmoothingEnabled = false;
  columns.forEach((column, index) => {
    ctx.drawImage(
      sheet,
      column * frameWidth,
      row * frameHeight,
      frameWidth,
      frameHeight,
      index * frameWidth,
      0,
      frameWidth,
      frameHeight,
    );
  });

  return canvas.toDataURL('image/png');
}

function paintVocations(): void {
  for (const element of document.querySelectorAll<HTMLElement>('[data-vocacao]')) {
    const vocation = Number(element.dataset['vocacao']) as Vocation;
    // Faixa 2 — a mais decorada: é a que mostra ombreira, manto, pluma e gema, e portanto a
    // que mais separa uma classe da outra, que é o trabalho desta seção.
    const sheet = characterSheet(vocation, 2);
    const url = strip(sheet, FRAME_WIDTH, FRAME_HEIGHT, WALK_COLUMNS, Direction.South);
    element.style.setProperty('--walk', `url(${url})`);
  }
}

function paintCreatures(): void {
  for (const element of document.querySelectorAll<HTMLElement>('[data-bicho]')) {
    const outfitId = Number(element.dataset['bicho']);
    const sheet = creatureSheet(outfitId);
    const width = sheet.width / CREATURE_COLUMNS;
    const height = sheet.height / CREATURE_ROWS;

    // Quem anda em quatro patas só tem silhueta de perfil; gente se apresenta de frente.
    const row = element.dataset['direcao'] === 'leste' ? Direction.East : Direction.South;

    element.style.setProperty('--s', `url(${strip(sheet, width, height, IDLE_COLUMNS, row)})`);
    element.style.setProperty('--w', String(width));
    element.style.setProperty('--h', String(height));
  }
}

/**
 * Revelação ao rolar.
 *
 * Desligada de saída para quem pediu menos movimento no sistema — e nesse caso os blocos
 * nascem visíveis, em vez de nascerem transparentes esperando um observador que não vai
 * chegar. É a diferença entre "sem animação" e "página em branco".
 */
function revealOnScroll(): void {
  const blocks = document.querySelectorAll<HTMLElement>('.rise');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduced || !('IntersectionObserver' in window)) {
    blocks.forEach((block) => block.classList.add('seen'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('seen');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px' },
  );

  blocks.forEach((block) => observer.observe(block));
}

paintVocations();
paintCreatures();
revealOnScroll();
